import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

/**
 * A filter chip that opens a checklist (28 Oct 2026, lead page).
 *
 * The chip reads "Owner" when nothing is picked, "Pradeep" with one pick and
 * "Owner · 3" with several — never "Owner: All" / "Owner: Pradeep" in every
 * option, which is what the plain select in FilterBar shows.
 *
 * value: array of option values ([] = all). `single` makes it pick one (a click
 * on the picked one clears it). Long lists get a search box.
 */
export default function MultiFilter({
  label, options = [], value = [], onChange, single = false, searchable, allLabel = 'All', className = '',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const picked = useMemo(() => new Set((value || []).map(String)), [value]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  useEffect(() => { if (!open) setQ(''); }, [open]);

  const showSearch = searchable ?? options.length > 8;
  const shown = q.trim() ? options.filter((o) => String(o.label).toLowerCase().includes(q.trim().toLowerCase())) : options;
  const toggle = (v) => {
    const key = String(v);
    if (single) { onChange?.(picked.has(key) ? [] : [key]); setOpen(false); return; }
    const next = new Set(picked);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange?.(options.map((o) => String(o.value)).filter((x) => next.has(x)));
  };
  const n = picked.size;
  const one = n === 1 ? options.find((o) => picked.has(String(o.value))) : null;
  const text = n === 0 ? label : one ? one.label : `${label} · ${n}`;

  return (
    <div className={`c-mf ${className}`} ref={ref}>
      <button
        type="button"
        className={`c-mf-btn${n ? ' is-set' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={n ? `${label}: ${n} picked` : `${label}: ${allLabel}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="c-mf-text">{text}</span>
        {n > 0
          ? <span role="button" tabIndex={-1} className="c-mf-x" aria-label={`Clear ${label}`} onClick={(e) => { e.stopPropagation(); onChange?.([]); }}><X size={14} /></span>
          : <ChevronDown size={14} aria-hidden="true" />}
      </button>
      {open && (
        <div className="c-mf-pop" role="listbox" aria-multiselectable={!single} aria-label={label}>
          <div className="c-mf-head">
            <b>{label}</b>
            {n > 0 && <button type="button" onClick={() => onChange?.([])}>Clear</button>}
          </div>
          {showSearch && (
            <input className="c-input c-mf-search" autoFocus placeholder={`Find ${label.toLowerCase()}…`} value={q} onChange={(e) => setQ(e.target.value)} />
          )}
          <div className="c-mf-list">
            {shown.length === 0 && <p className="c-mf-empty">Nothing matches</p>}
            {shown.map((o) => {
              const on = picked.has(String(o.value));
              return (
                <button key={o.value} type="button" role="option" aria-selected={on} className={`c-mf-opt${on ? ' is-on' : ''}`} onClick={() => toggle(o.value)}>
                  <span className={`c-mf-box${single ? ' is-radio' : ''}`}>{on && <Check size={12} />}</span>
                  <span className="c-mf-label">{o.label}</span>
                  {o.hint != null && <span className="c-mf-hint">{o.hint}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
